/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext } from '../text/text.component';

interface TableButtonProps extends Omit<ButtonCoreProps, 'style' | 'className'> {
  /** Whether the button is destructive. @default false */
  isDestructive?: boolean;
  style?: stylex.StyleXStyles;
}

const TableButtonContext = createContext<ContextValue<TableButtonProps, HTMLButtonElement>>({});

const TableButton = forwardRef((props: TableButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, TableButtonContext);

  const { style, isDestructive = false, children, ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      {...stylex.props(styles.base, transitionStyles.base, isDestructive && styles.destructive, style)}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          (renderProps.isDisabled && 'disabled') ||
          ((renderProps.isHovered || renderProps.isPressed) && isDestructive && 'critical') ||
          ((renderProps.isHovered || renderProps.isPressed) && !isDestructive && 'interactive') ||
          'tertiary'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                style: transitionStyles.base,
                size: 12,
                color: contentColor
              }],
              [IconContext, {
                size: 16,
                style: transitionStyles.base,
                color: contentColor
              }],
            ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    borderStyle: 'none',
    gap: 2,
    paddingInline: 8,
    alignItems: 'center',
    backgroundColor: {
      default: $.surface04,
      ':is([data-disabled])': $.buttonSurfaceDisabled,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceTertiaryHover,
    },
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-flex',
    flexShrink: 0,
    height: 32,
  },
  destructive: {
    backgroundColor: {
      default: $.surface04,
      ':is([data-hovered], [data-pressed])': $.surfaceCriticalMedium,
    },
  },
});

const transitionStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'background-color, stroke, fill, color',
    transitionTimingFunction: 'easy-in-out',
  },
});

export { TableButton, TableButtonContext, type TableButtonProps };
